/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { oneOf } from 'app/shared/lib/utilities';
import { bindable } from 'aurelia-typed-observable-plugin';

export interface IScrollTarget {

    scrollIntoView(): void;

}

@autoinject
export class EssScrollCustomAttribute implements IScrollTarget {

    @bindable.string({ primaryProperty: true })
    public align: string = 'start';

    @bindable.number()
    public margin: number = 24;

    @bindable.number()
    public delay: number = 50;

    @bindable.boolean()
    public local: boolean = false;

    private timeoutHandle: number = 0;

    constructor(protected readonly element: Element) { }

    public scrollIntoView(): void {
        clearTimeout(this.timeoutHandle);
        this.timeoutHandle = window.setTimeout(() => {
            if(this.local) {
                this.performLocalScroll();
            }
            else {
                this.performWindowScroll();
            }
            this.timeoutHandle = 0;
        }, this.delay);
    }

    private performLocalScroll(): void {
        this.element.scrollIntoView({
            behavior: 'smooth',
            block: oneOf(this.align, ['start', 'center', 'end', 'nearest'])
        });
    }

    private performWindowScroll(): void {
        let offset = 0;
        const header = document.getElementById('app-header');
        if(header) {
            offset += header.getBoundingClientRect().height;
        }

        // reset scroll position (browser remembers scroll position beyond)
        const maxScrollY = document.body.getBoundingClientRect().height - window.innerHeight;
        const scrollY = window.pageYOffset;
        if(scrollY >= maxScrollY) {
            // scroll up by one tenth of a pixel to get browser to reset internal position
            window.scrollTo({ behavior: 'auto', top: scrollY - 0.1 });
        }

        const target = document.documentElement.getBoundingClientRect();
        const bounds = this.element.getBoundingClientRect();

        const newScrollY = scrollY + this.getVerticalCoordinate(this.align, bounds, target, offset);
        window.scrollTo({ behavior: 'smooth', top: newScrollY });
    }

    private getVerticalCoordinate(align: string, bounds: ClientRect, target: ClientRect, offset: number): number {
        switch(align) {
            case 'bottom':
                return bounds.bottom - target.height + this.margin;
            case 'center':
                return 0.5 * (bounds.top + bounds.bottom - target.height - offset);
            case 'nearest':
                const top = this.getVerticalCoordinate('top', bounds, target, offset);
                const bottom = this.getVerticalCoordinate('bottom', bounds, target, offset);
                return (Math.abs(top) <= Math.abs(bottom)) ? top : bottom;
            case 'top':
            default:
                return bounds.top - this.margin - offset;
        }
    }

}
